import { ConfirmModal } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { cssVar } from '@streamlain/theme';
import { useCallback, useEffect, useState } from 'react';

/**
 * Screenshot confirmation request
 */
export interface ScreenshotConfirmRequest {
  /** Number of DOM blocks (notes, frames, images) - require slow html2canvas */
  domBlockCount: number;
  /** Number of shapes - use fast Canvas 2D API, negligible time */
  shapeCount: number;
  resolve: (confirmed: boolean) => void;
}

/**
 * Global event name for screenshot confirmation
 */
export const SCREENSHOT_CONFIRM_EVENT = 'affine-screenshot-confirm';

/**
 * Dispatch screenshot confirmation request
 * Returns a Promise that resolves to true (confirmed) or false (cancelled)
 *
 * @param domBlockCount - Number of DOM blocks (notes, frames, images) that need html2canvas
 * @param shapeCount - Number of shapes that use fast Canvas 2D API (optional, for display only)
 */
export function requestScreenshotConfirm(
  domBlockCount: number,
  shapeCount: number = 0
): Promise<boolean> {
  return new Promise(resolve => {
    window.dispatchEvent(
      new CustomEvent<ScreenshotConfirmRequest>(SCREENSHOT_CONFIRM_EVENT, {
        detail: { domBlockCount, shapeCount, resolve },
      })
    );
  });
}

/**
 * Styled keyboard shortcut component
 */
function KeyboardShortcut({ keys }: { keys: string[] }) {
  return (
    <span style={{ display: 'inline-flex', gap: '2px' }}>
      {keys.map((key, index) => (
        <span
          key={index}
          style={{
            minWidth: '24px',
            height: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 6px',
            borderRadius: '4px',
            background: cssVar('backgroundTertiaryColor'),
            fontSize: cssVar('fontXs'),
            fontFamily: 'monospace',
          }}
        >
          {key}
        </span>
      ))}
    </span>
  );
}

/**
 * Detect if running on macOS
 */
function isMacOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Use userAgentData if available, fallback to userAgent
  const ua = navigator.userAgent?.toLowerCase() || '';
  return ua.includes('mac');
}

/**
 * Provider component that listens for screenshot confirmation requests
 * and shows a confirmation dialog
 *
 * Must be mounted somewhere in the React tree (e.g., in ModalProvider or similar)
 */
export function ScreenshotConfirmProvider() {
  const t = useI18n();
  const [request, setRequest] = useState<ScreenshotConfirmRequest | null>(null);

  useEffect(() => {
    const handler = (event: CustomEvent<ScreenshotConfirmRequest>) => {
      setRequest(event.detail);
    };

    window.addEventListener(SCREENSHOT_CONFIRM_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(
        SCREENSHOT_CONFIRM_EVENT,
        handler as EventListener
      );
    };
  }, []);

  const handleConfirm = useCallback(() => {
    request?.resolve(true);
    setRequest(null);
  }, [request]);

  const handleCancel = useCallback(() => {
    request?.resolve(false);
    setRequest(null);
  }, [request]);

  const isMac = isMacOS();
  const domBlockCount = request?.domBlockCount || 0;
  const shapeCount = request?.shapeCount || 0;
  // Only DOM blocks need slow html2canvas (~5s each)
  // Shapes use fast Canvas 2D API (~20ms total) - negligible
  const estimatedSeconds = domBlockCount * 5;

  // TODO: Add i18n keys for these strings
  // Keys to add: com.streamlain.ai.screenshot.confirm.title,
  // com.streamlain.ai.screenshot.confirm.description, com.streamlain.ai.screenshot.confirm.tip,
  // com.streamlain.ai.screenshot.confirm.button
  const title = 'Generate Screenshot';

  // Show breakdown: DOM blocks (slow) + shapes (fast)
  const elementBreakdown =
    shapeCount > 0
      ? `${domBlockCount} blocks + ${shapeCount} shapes`
      : `${domBlockCount} blocks`;
  const descriptionText = `You have selected ${elementBreakdown}. Generating a screenshot may take approximately ${estimatedSeconds} seconds.`;

  const tipText =
    'Tip: Taking a manual screenshot and pasting it into the AI chat is much faster:';

  const confirmText = 'Generate Screenshot';

  const cancelText = t['Cancel']();

  return (
    <ConfirmModal
      open={!!request}
      title={title}
      description={
        <>
          <span style={{ display: 'block', marginBottom: '16px' }}>
            {descriptionText}
          </span>
          <span
            style={{
              display: 'block',
              marginBottom: '8px',
              color: cssVar('textSecondaryColor'),
              fontSize: cssVar('fontSm'),
            }}
          >
            {tipText}
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: cssVar('textSecondaryColor'),
              fontSize: cssVar('fontSm'),
            }}
          >
            {isMac ? (
              <>
                <span>macOS:</span>
                <KeyboardShortcut keys={['⌘', '⇧', '⌃', '4']} />
                <span style={{ marginLeft: '12px' }}>→</span>
                <KeyboardShortcut keys={['⌘', 'V']} />
              </>
            ) : (
              <>
                <span>Windows:</span>
                <KeyboardShortcut keys={['Win', 'Shift', 'S']} />
                <span style={{ marginLeft: '12px' }}>→</span>
                <KeyboardShortcut keys={['Ctrl', 'V']} />
              </>
            )}
          </span>
        </>
      }
      confirmText={confirmText}
      cancelText={cancelText}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      onOpenChange={open => {
        if (!open) {
          handleCancel();
        }
      }}
    />
  );
}
